import { useCallback, useEffect, useRef, useState } from 'react';
import { PANEL_WIDTH } from './aiAssistant.styles';

const STORAGE_KEY = 'aiAssistant.panelWidth';
/** The panel can grow up to this share of the window. */
const MAX_VIEWPORT_SHARE = 0.4;
const KEYBOARD_STEP = 24;

/** Never narrower than the default, never wider than 40% — unless the window itself is narrower than the default. */
const clampWidth = (width) => {
    const viewport = window.innerWidth;
    const max = Math.max(PANEL_WIDTH, Math.round(viewport * MAX_VIEWPORT_SHARE));
    return Math.min(Math.max(width, PANEL_WIDTH), max, viewport);
};

const readStoredWidth = () => {
    try {
        const stored = Number(localStorage.getItem(STORAGE_KEY));
        return clampWidth(stored || PANEL_WIDTH);
    } catch {
        return clampWidth(PANEL_WIDTH);
    }
};

const storeWidth = (width) => {
    try {
        localStorage.setItem(STORAGE_KEY, String(width));
    } catch {
        // Storage can be blocked; the width just won't be remembered.
    }
};

/**
 * Drag-to-resize for the right-anchored panel. Returns the width and the props
 * for a handle on the panel's left edge (pointer drag, double-click to toggle
 * default/max, arrow keys for keyboard users). The chosen width is remembered
 * per browser.
 */
export const usePanelWidth = () => {
    const [width, setWidth] = useState(readStoredWidth);
    const [isResizing, setIsResizing] = useState(false);
    const widthRef = useRef(width);
    widthRef.current = width;

    const commit = useCallback((next) => {
        const clamped = clampWidth(next);
        setWidth(clamped);
        storeWidth(clamped);
    }, []);

    // Re-clamp when the window shrinks, so the panel never exceeds 40% of it.
    useEffect(() => {
        const onResize = () => setWidth((current) => clampWidth(current));
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    const onPointerDown = useCallback((event) => {
        if (event.button !== 0) return;
        event.preventDefault();
        const handle = event.currentTarget;
        handle.setPointerCapture(event.pointerId);
        setIsResizing(true);

        // Text selection and the page's own cursors fight the drag otherwise.
        const { userSelect, cursor } = document.body.style;
        document.body.style.userSelect = 'none';
        document.body.style.cursor = 'col-resize';

        const onMove = (moveEvent) => setWidth(clampWidth(window.innerWidth - moveEvent.clientX));
        const onUp = () => {
            handle.removeEventListener('pointermove', onMove);
            handle.removeEventListener('pointerup', onUp);
            handle.removeEventListener('pointercancel', onUp);
            document.body.style.userSelect = userSelect;
            document.body.style.cursor = cursor;
            setIsResizing(false);
            storeWidth(widthRef.current);
        };

        handle.addEventListener('pointermove', onMove);
        handle.addEventListener('pointerup', onUp);
        handle.addEventListener('pointercancel', onUp);
    }, []);

    const onDoubleClick = useCallback(() => {
        commit(widthRef.current > PANEL_WIDTH ? PANEL_WIDTH : Number.MAX_SAFE_INTEGER);
    }, [commit]);

    const onKeyDown = useCallback(
        (event) => {
            if (event.key === 'ArrowLeft') commit(widthRef.current + KEYBOARD_STEP);
            else if (event.key === 'ArrowRight') commit(widthRef.current - KEYBOARD_STEP);
            else return;
            event.preventDefault();
        },
        [commit],
    );

    return {
        width,
        isResizing,
        handleProps: {
            role: 'separator',
            'aria-orientation': 'vertical',
            'aria-label': 'Resize assistant panel',
            'aria-valuenow': width,
            tabIndex: 0,
            onPointerDown,
            onDoubleClick,
            onKeyDown,
        },
    };
};
